import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { archiveAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Archive,
  FolderOpen,
  FileText,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { formatDate } from '../../lib/utils';

const ArchivesPage = () => {
  const [loading, setLoading] = useState(true);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [archivedFiles, setArchivedFiles] = useState([]);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    try {
      const [projectsRes, filesRes] = await Promise.all([
        archiveAPI.getArchivedProjects(),
        archiveAPI.getArchivedFiles(),
      ]);
      setArchivedProjects(projectsRes.data);
      setArchivedFiles(filesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des archives');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreProject = async (projectId) => {
    setRestoring(projectId);
    try {
      await archiveAPI.restoreProject(projectId);
      toast.success('Projet restauré');
      fetchArchives();
    } catch (error) {
      toast.error('Erreur lors de la restauration');
    } finally {
      setRestoring(null);
    }
  };

  const handleRestoreFile = async (fileId) => {
    setRestoring(fileId);
    try {
      await archiveAPI.restoreFile(fileId);
      toast.success('Fichier restauré');
      fetchArchives();
    } catch (error) {
      toast.error('Erreur lors de la restauration');
    } finally {
      setRestoring(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="archives-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Archive className="w-6 h-6" />
          Archives
        </h1>
        <p className="text-slate-600">Projets et fichiers archivés.</p>
      </div>

      <Tabs defaultValue="projets">
        <TabsList>
          <TabsTrigger value="projets">
            <FolderOpen className="w-4 h-4 mr-2" />
            Projets ({archivedProjects.length})
          </TabsTrigger>
          <TabsTrigger value="fichiers">
            <FileText className="w-4 h-4 mr-2" />
            Fichiers ({archivedFiles.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projets" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Projets archivés</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {archivedProjects.length === 0 ? (
                <div className="py-12 text-center">
                  <Archive className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aucun projet archivé.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Projet</TableHead>
                      <TableHead className="hidden md:table-cell">Client</TableHead>
                      <TableHead className="hidden md:table-cell">Archivé le</TableHead>
                      <TableHead className="w-32">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedProjects.map((project) => (
                      <TableRow key={project.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FolderOpen className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{project.nom}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-slate-500">
                          {project.client_nom || '-'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-slate-500">
                          {formatDate(project.archived_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreProject(project.id)}
                            disabled={restoring === project.id}
                          >
                            {restoring === project.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Restaurer
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fichiers" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Fichiers archivés</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {archivedFiles.length === 0 ? (
                <div className="py-12 text-center">
                  <Archive className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aucun fichier archivé.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fichier</TableHead>
                      <TableHead className="hidden md:table-cell">Projet</TableHead>
                      <TableHead className="hidden md:table-cell">Archivé le</TableHead>
                      <TableHead className="w-32">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedFiles.map((file) => (
                      <TableRow key={file.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{file.nom_fichier_original}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-slate-500">
                          {file.project_nom || '-'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-slate-500">
                          {formatDate(file.archived_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreFile(file.id)}
                            disabled={restoring === file.id}
                          >
                            {restoring === file.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Restaurer
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ArchivesPage;
